import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { api, formatTarih } from '../../lib/api';
import { DegisiklikListesi, IS_AD, Kart, SayfaBaslik, Sonuc, YayinDugmesi, sonYayinMetni, useSiteDurum, type Degisiklik } from './ortak';

export type YayinKaydi = {
  id: number; tur: 'YAYIN' | 'ONIZLEME' | 'GERI_AL'; kuru: boolean; baslangic: string; bitis: string | null; sureMs: number | null;
  ok: boolean | null; kullanici: string | null; cikti: string; degisiklikler: Degisiklik[]; not: string | null;
  derlemeMs: number | null; sayfaSayisi: number | null; geriAlinan: number | null; yayinda: boolean; geriDonulebilir: boolean;
};
export type Gecmis = { hatKurulu: boolean; eksikAyar: string[]; panelOncesi: boolean; gecmis: YayinKaydi[] };

export const sure = (ms: number | null) => (ms == null ? '—' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} sn`);

export function SonucRozeti({ k }: { k: YayinKaydi }) {
  return (
    <>
      {k.ok == null ? <span className="ccd-badge ccd-badge-warning">Sürüyor</span>
        : k.ok ? <span className="ccd-badge ccd-badge-success">Başarılı</span>
        : <span className="ccd-badge ccd-badge-danger">Başarısız</span>}
      {k.yayinda && <span className="ccd-badge" style={{ marginLeft: 6 }}>Yayında</span>}
    </>
  );
}

/** Site › Yayın geçmişi — yapılan yayınlar, ayrıntıları ve "Bu sürüme geri dön". */
export default function SiteYayinGecmisi() {
  const { durum, yenile } = useSiteDurum();
  const [g, setG] = useState<Gecmis | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [acik, setAcik] = useState<number | null>(null);
  const [mesgul, setMesgul] = useState(false);

  const getir = useCallback(() => api<Gecmis>('/api/site/yayin').then(setG).catch((e) => setHata((e as Error).message)), []);
  useEffect(() => { void getir(); }, [getir]);

  // İş bittiğinde geçmiş yenilenir ve sonucu tepeye yazılır.
  const suren = durum?.calisan?.id ?? null;
  const onceki = useRef<number | null>(null);
  useEffect(() => {
    const biten = onceki.current;
    onceki.current = suren;
    if (biten == null || suren != null) return;
    void api<Gecmis>('/api/site/yayin').then((yeni) => {
      setG(yeni);
      const k = yeni.gecmis.find((x) => x.id === biten);
      if (!k) return;
      if (k.ok) { setHata(null); setBilgi(`${IS_AD[k.tur]} tamamlandı (${sure(k.sureMs)}${k.sayfaSayisi ? `, ${k.sayfaSayisi} sayfa` : ''}).${k.kuru ? ' Canlı siteye dokunulmadı.' : ''}`); }
      else { setBilgi(null); setHata(`${IS_AD[k.tur]} başarısız oldu — canlı site değişmedi.`); setAcik(k.id); }
    }).catch(() => undefined);
  }, [suren]);

  async function calistir(yol: string, govde?: unknown) {
    setBilgi(null); setHata(null); setMesgul(true);
    try { await api(yol, { method: 'POST', ...(govde ? { body: JSON.stringify(govde) } : {}) }); await yenile(); }
    catch (e) { setHata(e instanceof Error ? e.message : 'Başlatılamadı'); }
    finally { setMesgul(false); }
  }
  function geriDon(id: number, ad: string) {
    if (!window.confirm(`Canlı site ${ad} döndürülecek. Paneldeki taslağınız değişmez. Devam edilsin mi?`)) return;
    void calistir(`/api/site/yayin/${id}/geri-al`);
  }

  const kurulu = durum?.hatKurulu ?? false;
  const kapali = !kurulu || Boolean(durum?.calisan) || mesgul;
  const neden = !kurulu ? 'Site yayın hattı bu sunucuda kurulmamış' : durum?.calisan ? 'Bir iş sürüyor — bitmesini bekleyin' : undefined;

  return (
    <div className="ccd-stack">
      <SayfaBaslik baslik="Yayın geçmişi" durum={durum} alt={durum ? sonYayinMetni(durum) : undefined}>
        <button type="button" className="ccd-btn" disabled={kapali} title={neden ?? 'Siteyi derler, canlıya yazmaz'} onClick={() => void calistir('/api/site/yayin', { kuru: true })}>Derlemeyi dene</button>
        <YayinDugmesi durum={durum} yenile={() => { void yenile(); void getir(); }} />
      </SayfaBaslik>
      <Sonuc bilgi={bilgi} hata={hata} />

      {durum && !kurulu && (
        <div className="ccd-site-not" role="status">
          Site yayın hattı bu sunucuda kurulmamış (eksik ayar: <span className="ccd-mono">{durum.eksikAyar.join(', ')}</span>). İçerik düzenlenebilir; yayın kurulumdan sonra açılır.
        </div>
      )}

      <Kart className="ccd-card-flush ccd-site-dizin-kart">
        {!g ? <div className="ccd-site-bosluk ccd-inline-note">Yükleniyor…</div> : g.gecmis.length === 0 ? (
          <div className="ccd-site-bosluk"><div className="ccd-site-kart-baslik">Panelden henüz yayın yapılmadı</div></div>
        ) : (
          <div className="ccd-site-dizin-sar">
            <table className="ccd-table ccd-site-dizin">
              <thead>
                <tr><th className="ccd-th ccd-site-dar-gizle">#</th><th className="ccd-th">İşlem</th><th className="ccd-th">Sonuç</th><th className="ccd-th ccd-site-dar-gizle">Kim</th><th className="ccd-th">Ne zaman</th><th className="ccd-th ccd-site-dar-gizle">Süre</th><th className="ccd-th" /></tr>
              </thead>
              <tbody>
                {g.gecmis.map((k) => (
                  <Fragment key={k.id}>
                    <tr className="ccd-tr ccd-tr-clickable" onClick={() => setAcik(acik === k.id ? null : k.id)}>
                      <td className="ccd-td ccd-td-num ccd-site-dar-gizle">{k.id}</td>
                      <td className="ccd-td">{IS_AD[k.tur]}{k.geriAlinan != null && <span className="ccd-meta"> → {k.geriAlinan === 0 ? 'panel öncesi site' : `#${k.geriAlinan}`}</span>}
                        {k.degisiklikler.length > 0 && <span className="ccd-meta"> · {k.degisiklikler.length} değişiklik</span>}</td>
                      <td className="ccd-td ccd-td-nowrap"><SonucRozeti k={k} /></td>
                      <td className="ccd-td ccd-site-dar-gizle">{k.kullanici ?? <span className="ccd-muted">—</span>}</td>
                      <td className="ccd-td ccd-td-nowrap">{formatTarih(k.baslangic)}</td>
                      <td className="ccd-td ccd-td-nowrap ccd-site-dar-gizle">{sure(k.sureMs)}</td>
                      <td className="ccd-td ccd-td-nowrap" onClick={(e) => e.stopPropagation()}>
                        {k.ok && !k.kuru && !k.yayinda ? (
                          <button type="button" className="ccd-site-metin-dugme" disabled={kapali || !k.geriDonulebilir}
                                  title={!k.geriDonulebilir ? 'Bu sürümün yedeği artık yok (yalnız son 5 yedek tutulur)' : neden}
                                  onClick={() => geriDon(k.id, `#${k.id} numaralı sürüme`)}>Bu sürüme geri dön</button>
                        ) : <span className="ccd-site-metin-dugme" aria-hidden="true">{acik === k.id ? 'Gizle' : 'Ayrıntı'}</span>}
                      </td>
                    </tr>
                    {acik === k.id && (
                      <tr className="ccd-tr">
                        <td className="ccd-td ccd-site-ayrinti" colSpan={7}>
                          <div className="ccd-stack-sm">
                            {k.not && <div>{k.not}</div>}
                            {k.degisiklikler.length > 0 && <DegisiklikListesi ozet={k.degisiklikler} />}
                            {k.derlemeMs != null && <div className="ccd-meta">Derleme: {sure(k.derlemeMs)} · {k.sayfaSayisi} sayfa</div>}
                            <pre className="ccd-site-cikti ccd-scroll">{k.cikti || 'çıktı yok'}</pre>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Kart>

      {g?.panelOncesi && (
        <div className="ccd-card ccd-site-serit-kart">
          <div className="ccd-meta">Panelden ilk yayından önceki site (elle yüklenen sürüm) yedekte duruyor.</div>
          <button type="button" className="ccd-btn ccd-btn-sm" disabled={kapali} title={neden} onClick={() => geriDon(0, 'panelden önceki hâline')}>Panel öncesi siteye dön</button>
        </div>
      )}
      <div className="ccd-field-hint">Her yayından önce canlı sitenin yedeği alınır; son 5 yedek tutulur. Derleme başarısızsa canlı siteye dokunulmaz.</div>
    </div>
  );
}
